/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictProvider = require('pict-provider');
const libLocationTree = require('headlight-sample-scanning').LocationTree;

class LocationProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	/**
	 * Every location the signed-in customer can see, read a page at a time so no list is cut short.
	 * @param {number} [pPageSize] - Default 1000.
	 * @return {Promise<Array<Object>>}
	 */
	async getAllLocations(pPageSize = 1000)
	{
		const tmpLocations = [];
		for (let tmpOffset = 0; ; tmpOffset += pPageSize)
		{
			const tmpPage = await new Promise((fResolve, fReject) =>
			{
				this.pict.HeadlightRestClient.getJSON(`Locations/FilteredTo/FBV~IDLocation~GT~0/${ tmpOffset }/${ pPageSize }`, (pError, pBody) => (pError ? fReject(pError) : fResolve(Array.isArray(pBody) ? pBody : [])));
			});
			tmpLocations.push(...tmpPage);
			if (tmpPage.length < pPageSize)
			{
				return tmpLocations;
			}
		}
	}

	/**
	 * Samples whose current location (their newest location join) is this location or any location inside it.
	 * @param {number} pIDLocation
	 * @param {Array<Object>} pLocations - The customer's locations, for walking the tree.
	 * @param {number} [pLimit] - Most samples to return. Default 500.
	 * @return {Promise<{ Samples: Array<Object>, LocationIDs: Array<number>, Truncated: boolean }>}
	 */
	async getSamplesAtLocation(pIDLocation, pLocations, pLimit = 500)
	{
		const tmpLocationIDs = [ Number(pIDLocation) ].concat(libLocationTree.getDescendantIDs(pIDLocation, pLocations));
		const tmpLocationSet = new Set(tmpLocationIDs);
		const fGet = (pURL) => new Promise((fResolve, fReject) =>
		{
			this.pict.HeadlightRestClient.getJSON(pURL, (pError, pBody) => (pError ? fReject(pError) : fResolve(Array.isArray(pBody) ? pBody : [])));
		});
		const fBatches = (pIDs) =>
		{
			const tmpBatches = [];
			for (let i = 0; i < pIDs.length; i += 100)
			{
				tmpBatches.push(pIDs.slice(i, i + 100));
			}
			return tmpBatches;
		};
		const tmpCandidateIDs = new Set();
		for (const tmpBatch of fBatches(tmpLocationIDs))
		{
			for (const tmpJoin of await fGet(`SampleLocationJoins/FilteredTo/FBL~IDLocation~INN~${ tmpBatch.join(',') }/0/5000`))
			{
				tmpCandidateIDs.add(Number(tmpJoin.IDSample));
			}
		}
		// A sample can keep older joins after it moves; it is here only if its newest join is.
		const tmpCurrentIDs = [];
		for (const tmpBatch of fBatches(Array.from(tmpCandidateIDs)))
		{
			const tmpNewest = new Map();
			for (const tmpJoin of await fGet(`SampleLocationJoins/FilteredTo/FBL~IDSample~INN~${ tmpBatch.join(',') }~FSF~CreateDate~DESC~0/0/10000`))
			{
				if (!tmpNewest.has(Number(tmpJoin.IDSample)))
				{
					tmpNewest.set(Number(tmpJoin.IDSample), Number(tmpJoin.IDLocation));
				}
			}
			for (const [ tmpIDSample, tmpIDLocation ] of tmpNewest)
			{
				if (tmpLocationSet.has(tmpIDLocation))
				{
					tmpCurrentIDs.push({ IDSample: tmpIDSample, IDLocation: tmpIDLocation });
				}
			}
		}
		const tmpTruncated = tmpCurrentIDs.length > pLimit;
		const tmpWanted = tmpCurrentIDs.slice(0, pLimit);
		const tmpLocationBySample = new Map(tmpWanted.map((pRow) => [ pRow.IDSample, pRow.IDLocation ]));
		const tmpSamples = [];
		for (const tmpBatch of fBatches(tmpWanted.map((pRow) => pRow.IDSample)))
		{
			for (const tmpSample of await fGet(`Samples/FilteredTo/FBL~IDSample~INN~${ tmpBatch.join(',') }/0/100`))
			{
				tmpSamples.push(Object.assign({}, tmpSample, { CurrentIDLocation: tmpLocationBySample.get(Number(tmpSample.IDSample)) }));
			}
		}
		return { Samples: tmpSamples, LocationIDs: tmpLocationIDs, Truncated: tmpTruncated };
	}

	/**
	 * Get the Locations by Sample ID
	 * @param {Number} pIDSample - The ID of the Sample to get Locations for
	 * @param {Function} [pCallback] - The callback function to call when the Location is fetched
	 * @return {Promise}
	 * @async
	 */
	async getLocationsBySampleID(pIDSample, pCallback)
	{
		try
		{
			return await new Promise((resolve, reject) =>
			{
				// First check if there is already a join between these.
				this.pict.fable.services.HeadlightRestClient.getJSON(`SampleLocationJoins/FilteredTo/FBV~IDSample~EQ~${ pIDSample }~FSF~CreateDate~DESC~0`,
					async (pError, pBody) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error fetching Locations: `, pError);
							reject(pError);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(null);
							}
						}
						else
						{
							if (pBody.length)
							{
								const fullLocations = [];
								for (const j of pBody)
								{
									fullLocations.push(await this.getLocationByID(j.IDLocation));
								}
								resolve(fullLocations);
								if (pCallback)
								{
									pCallback(fullLocations);
								}
							}
							else
							{
								resolve([]);
								if (pCallback)
								{
									pCallback([]);
								}
							}
						}
					});
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error fetching Locations: `, error);
			// if we have a callback, run it now
			if (pCallback)
			{
				pCallback(null);
			}
		}
	}

	/**
	 * Get the Location by GUID
	 * @param {Number} pGUIDLocation - The GUID of the Location to get
	 * @param {Function} [pCallback] - The callback function to call when the Location is fetched
	 * @param {Number} [recursiveDepth] - The depth of current recursion
	 * @return {Promise}
	 * @async
	 */
	async getLocationByGUID(pGUIDLocation, pCallback, recursiveDepth = 0)
	{
		const maxRecursiveDepth = 1;
		try
		{
			return await new Promise((resolve, reject) =>
			{
				this.pict.fable.services.HeadlightRestClient.getJSON(`Locations/By/GUIDLocation/${ pGUIDLocation }`,
					async (pError, pBody) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error fetching Location: `, pError);
							reject(pError);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(null);
							}
						}
						else
						{
							pBody = pBody[0];
							if (pBody.ParentIDLocation && recursiveDepth < maxRecursiveDepth)
							{
								try
								{
									pBody.ParentLocation = await this.getLocationByID(pBody.ParentIDLocation, null, recursiveDepth + 1);
								}
								catch (pError)
								{
									this.pict.log.error(`Error fetching Location: `, pError);
									reject(pError);
									// if we have a callback, run it now
									if (pCallback)
									{
										pCallback(null);
									}
								}
							}
							resolve(pBody);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(pBody);
							}
						}
					});
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error fetching Location: `, error);
			// if we have a callback, run it now
			if (pCallback)
			{
				pCallback(null);
			}
		}
	}

	/**
	 * Get the Location by ID
	 * @param {Number} pIDLocation - The ID of the Location to get
	 * @param {Function} [pCallback] - The callback function to call when the Location is fetched
	 * @param {Number} [recursiveDepth] - The depth of current recursion
	 * @return {Promise}
	 * @async
	 */
	async getLocationByID(pIDLocation, pCallback, recursiveDepth = 0)
	{
		const maxRecursiveDepth = 1;
		try
		{
			return await new Promise((resolve, reject) =>
			{
				this.pict.fable.services.HeadlightRestClient.getJSON(`Location/${ pIDLocation }`,
					async (pError, pBody) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error fetching Location: `, pError);
							reject(pError);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(null);
							}
						}
						else
						{
							if (pBody.ParentIDLocation && recursiveDepth < maxRecursiveDepth)
							{
								try
								{
									pBody.ParentLocation = await this.getLocationByID(pBody.ParentIDLocation, null, recursiveDepth + 1);
								}
								catch (pError)
								{
									this.pict.log.error(`Error fetching Location: `, pError);
									reject(pError);
									// if we have a callback, run it now
									if (pCallback)
									{
										pCallback(null);
									}
								}
							}
							resolve(pBody);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(pBody);
							}
						}
					});
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error fetching Location: `, error);
			// if we have a callback, run it now
			if (pCallback)
			{
				pCallback(null);
			}
		}
	}

	/**
	 * Add a sample to a location.
	 * @param {Number} pIDSample - The ID of the Sample to add to the location
	 * @param {Number} pIDLocation - The ID of the Location to add the Sample to
	 * @param {Function} [pCallback] - The callback function to call when the action is complete
	 * @return {Promise}
	 * @async
	 */
	async addSampleToLocation(pIDSample, pIDLocation, pCallback)
	{
		try
		{
			return await new Promise((resolve, reject) =>
			{
				// First check if there is already a join between these.
				this.pict.fable.services.HeadlightRestClient.getJSON(`SampleLocationJoins/FilteredTo/FBV~IDSample~EQ~${ pIDSample }~FSF~CreateDate~DESC~0`,
					async (pError, pBody) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error adding sample to location: `, pError);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(null);
								return;
							}
							reject(pError);
						}
						else
						{
							// If we found a join already, return it.
							if (pBody?.length && pBody[0].IDLocation == pIDLocation)
							{
								resolve(pBody[0]);
								// if we have a callback, run it now
								if (pCallback)
								{
									pCallback(pBody[0]);
								}
							}
							else
							{
								// Otherwise, create the join and return that.
								this.pict.HeadlightRestClient.putJSON('SampleLocationJoin/Upsert', { IDSample: pIDSample, IDLocation: pIDLocation }, (pError, pBody) =>
								{
									if (pError)
									{
										this.pict.log.error(`Error adding sample to location: `, pError);
										// if we have a callback, run it now
										if (pCallback)
										{
											pCallback(null);
											return;
										}
										reject(pError);
									}
									else
									{
										this.pict.HeadlightRestClient.getJSON(`Sample/${ pIDSample }`, async (pError, pBody) =>
										{
											if (pError)
											{
												this.pict.log.error(`Error adding sample to location: `, pError);
												// if we have a callback, run it now
												if (pCallback)
												{
													pCallback(null);
													return;
												}
												reject(pError);
											}
											else
											{
												if (parseInt(pBody?.Extension?.StorageLocation) !== pIDLocation)
												{
													this.pict.HeadlightRestClient.putJSON('Sample',
													{
														IDSample: pIDSample,
														Extension: Object.assign(pBody.Extension, { StorageLocation: `${ pIDLocation }` }),
													}, async (pError, pBody) =>
													{
														if (pError)
														{
															this.pict.log.error(`Error adding sample to location: `, pError);
															// if we have a callback, run it now
															if (pCallback)
															{
																pCallback(null);
																return;
															}
															reject(pError);
														}
														else
														{
															resolve(pBody);
															// if we have a callback, run it now
															if (pCallback)
															{
																pCallback(pBody);
															}
														}
													});
												}
												else
												{
													resolve(pBody);
													// if we have a callback, run it now
													if (pCallback)
													{
														pCallback(pBody);
													}
												}
											}
										});
									}
								});
							}
						}
					});
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error adding Sample to Location: `, error);
			// if we have a callback, run it now
			if (pCallback)
			{
				pCallback(null);
			}
			else
			{
				throw error;
			}
		}
	}

	/**
	 * Remove a sample from a location.
	 * @param {Number} pIDSample - The ID of the Sample to remove from the location
	 * @param {Number} pIDLocation - The ID of the Location to remove the Sample from
	 * @param {Function} [pCallback] - The callback function to call when the action is complete
	 * @return {Promise}
	 * @async
	 */
	async removeSamplefromLocation(pIDSample, pIDLocation, pCallback)
	{
		try
		{
			return await new Promise((resolve, reject) =>
			{
				// First check if there is already a join between these.
				this.pict.fable.services.HeadlightRestClient.getJSON(`SampleLocationJoins/FilteredTo/FBV~IDSample~EQ~${ pIDSample }~FBV~IDLocation~EQ~${ pIDLocation }`,
					async (pError, pBody) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error removing sample from location: `, pError);
							reject(pError);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(null);
							}
						}
						else
						{
							// Delete every join for the pair; repeated moves can leave more than one.
							if (pBody?.length)
							{
								const tmpJoins = pBody.slice();
								const fDeleteNext = () =>
								{
									const tmpJoin = tmpJoins.shift();
									if (!tmpJoin)
									{
										resolve(true);
										// if we have a callback, run it now
										if (pCallback)
										{
											pCallback(true);
										}
										return;
									}
									this.pict.HeadlightRestClient.deleteEntity('SampleLocationJoin', tmpJoin.IDSampleLocationJoin, (pError) =>
									{
										if (pError)
										{
											this.pict.log.error(`Error removing sample from location: `, pError);
											reject(pError);
											// if we have a callback, run it now
											if (pCallback)
											{
												pCallback(null);
											}
											return;
										}
										fDeleteNext();
									});
								};
								fDeleteNext();
							}
							else
							{
								// Otherwise, just return;
								resolve(true);
								// if we have a callback, run it now
								if (pCallback)
								{
									pCallback(true);
								}
							}
						}
					});
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error removing Sample from Location: `, error);
			// if we have a callback, run it now
			if (pCallback)
			{
				pCallback(null);
			}
			else
			{
				throw error;
			}
		}
	}
}

module.exports = LocationProvider;
module.exports.default_configuration = {};
